import {
  ArrowRightIcon,
  BriefcaseIcon,
  BuildingsIcon,
  CheckCircleIcon,
  FileTextIcon,
  PencilSimpleIcon,
  TargetIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import type { CreateResumeAnswers, ManualStep } from "../types";

type Props = {
  answers: CreateResumeAnswers;
  onEdit: (step: ManualStep) => void;
  onContinue: () => void;
};

const profileLabels = {
  student: "Étudiant(e) ou jeune diplômé(e)",
  "first-job": "À la recherche d’un premier emploi",
  experienced: "Professionnel(le) expérimenté(e)",
  "career-change": "En reconversion professionnelle",
  independent: "Indépendant(e) ou consultant(e)",
};

const experienceLabels = {
  none: "Aucune",
  "under-2": "Moins de 2 ans",
  "2-to-5": "2 à 5 ans",
  "over-5": "Plus de 5 ans",
};

const applicationLabels = {
  job: "Emploi",
  internship: "Stage",
  "work-study": "Alternance",
  spontaneous: "Candidature spontanée",
};

export const SummaryStep = ({ answers, onEdit, onContinue }: Props) => {
  const rows = [
    {
      label: "Profil",
      value: answers.profile ? profileLabels[answers.profile] : "—",
      icon: UserIcon,
      step: 2 as ManualStep,
    },
    {
      label: "Expérience",
      value: answers.experience ? experienceLabels[answers.experience] : "—",
      icon: BriefcaseIcon,
      step: 2 as ManualStep,
    },
    {
      label: "Objectif",
      value: answers.targetJob || "—",
      icon: TargetIcon,
      step: 3 as ManualStep,
    },
    {
      label: "Secteur",
      value: answers.sector || "—",
      icon: BuildingsIcon,
      step: 3 as ManualStep,
    },
    {
      label: "Candidature",
      value: answers.applicationType ? applicationLabels[answers.applicationType] : "—",
      icon: BriefcaseIcon,
      step: 3 as ManualStep,
    },
  ];

  const hasJobDescription = Boolean(answers.jobDescription.trim());

  return (
    <section
      className={cn(
        "mx-auto flex h-full min-h-0 w-full max-w-3xl",
        "flex-col justify-center overflow-hidden",
      )}
    >
      <header className="shrink-0">
        <h1
          id="create-resume-title"
          className={cn(
            "text-2xl font-bold tracking-tight",
            "leading-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px]",
          )}
        >
          Votre parcours est prêt
        </h1>

        <p className={cn("mt-1 text-xs leading-5 text-indigo-950/75", "sm:text-sm")}>
          Vérifiez vos réponses avant de découvrir les modèles adaptés à votre profil.
        </p>
      </header>

      <div
        className={cn(
          "mt-3 shrink-0 overflow-hidden rounded-xl",
          "border border-indigo-100 bg-white",
          "px-3 sm:mt-4 sm:px-4",
          "shadow-[0_4px_14px_rgba(15,23,42,0.04)]",
        )}
      >
        {rows.map(({ label, value, icon: IconComponent, step }) => (
          <div
            key={label}
            className={cn(
              "grid min-h-10",
              "grid-cols-[auto_minmax(0,1fr)_auto]",
              "items-center gap-2",
              "border-b border-slate-100 py-1.5",
              "sm:min-h-11 sm:gap-3 sm:py-2",
            )}
          >
            <span
              className={cn(
                "grid size-7 shrink-0 place-items-center",
                "rounded-full bg-violet-50 text-indigo-700",
                "sm:size-8",
              )}
            >
              <IconComponent aria-hidden size={16} weight="duotone" className="sm:size-[17px]" />
            </span>

            <div
              className={cn(
                "min-w-0",
                "sm:grid sm:grid-cols-[115px_minmax(0,1fr)]",
                "sm:items-center sm:gap-3",
              )}
            >
              <p className={cn("text-[10px] font-bold text-indigo-950", "sm:text-xs")}>{label}</p>

              <p
                className={cn(
                  "truncate text-[10px] font-medium",
                  "text-indigo-950/75",
                  "sm:text-xs",
                )}
              >
                {value}
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                onEdit(step);
              }}
              aria-label={`Modifier ${label}`}
              className={cn(
                "grid size-7 shrink-0 place-items-center",
                "rounded-lg text-indigo-600",
                "transition-colors hover:bg-indigo-50",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
                "sm:size-8",
              )}
            >
              <PencilSimpleIcon aria-hidden size={16} weight="bold" />
            </button>
          </div>
        ))}

        {hasJobDescription && (
          <div
            className={cn(
              "my-2 flex items-center gap-2.5",
              "rounded-xl border border-emerald-100",
              "bg-emerald-50/70 px-3 py-2",
              "text-emerald-800",
              "sm:my-2.5 sm:py-2.5",
            )}
          >
            <span
              className={cn(
                "grid size-8 shrink-0 place-items-center",
                "rounded-lg bg-white/80 text-emerald-600",
              )}
            >
              <FileTextIcon aria-hidden size={20} weight="duotone" />
            </span>

            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "flex items-center gap-1.5",
                  "text-[10px] font-bold text-emerald-700",
                  "sm:text-xs",
                )}
              >
                Annonce d’emploi ajoutée
                <CheckCircleIcon aria-hidden size={15} weight="fill" className="shrink-0" />
              </p>

              <p
                className={cn(
                  "mt-0.5 truncate text-[9px]",
                  "leading-4 text-indigo-950/65",
                  "sm:text-[10px]",
                )}
              >
                Elle sera utilisée pour personnaliser les recommandations.
              </p>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          "mt-2.5 flex shrink-0 items-center gap-3",
          "rounded-xl border border-emerald-200",
          "bg-gradient-to-r from-emerald-50/90 to-white",
          "px-3 py-2.5",
          "sm:mt-3 sm:px-4 sm:py-3",
        )}
      >
        <span
          className={cn(
            "grid size-12 shrink-0 place-items-center",
            "rounded-full border border-emerald-100",
            "bg-white text-emerald-600",
            "shadow-[0_4px_12px_rgba(16,185,129,0.10)]",
            "sm:size-14",
          )}
        >
          <TargetIcon aria-hidden size={28} weight="duotone" className="sm:size-8" />
        </span>

        <div className="min-w-0 flex-1">
          <p className={cn("text-[11px] font-bold text-indigo-950", "sm:text-xs")}>
            Votre sélection sera personnalisée
          </p>

          <ul
            className={cn(
              "mt-1 space-y-0.5",
              "text-[9px] leading-4 text-indigo-950/75",
              "sm:text-[10px]",
            )}
          >
            <li className="flex items-center gap-1.5">
              <CheckCircleIcon
                aria-hidden
                size={14}
                weight="fill"
                className="shrink-0 text-emerald-500"
              />

              <span className="truncate">Des modèles adaptés à votre niveau d’expérience</span>
            </li>

            <li className="flex items-center gap-1.5">
              <CheckCircleIcon
                aria-hidden
                size={14}
                weight="fill"
                className="shrink-0 text-emerald-500"
              />

              <span className="truncate">Des suggestions liées au poste recherché</span>
            </li>

            {hasJobDescription && (
              <li className="flex items-center gap-1.5">
                <CheckCircleIcon
                  aria-hidden
                  size={14}
                  weight="fill"
                  className="shrink-0 text-emerald-500"
                />

                <span className="truncate">Des recommandations basées sur l’annonce</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className={cn("mt-3 flex shrink-0 items-center justify-center gap-3", "sm:mt-4")}>
        <button
          type="button"
          onClick={() => {
            onEdit(2);
          }}
          className={cn(
            "min-h-10 shrink-0 rounded-xl px-2",
            "text-[10px] font-bold text-indigo-700",
            "transition-colors hover:bg-indigo-50",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
            "sm:px-3 sm:text-xs",
          )}
        >
          Modifier mes réponses
        </button>

        <button
          type="button"
          onClick={onContinue}
          className={cn(
            "flex min-h-10 min-w-0 flex-1",
            "max-w-[350px] items-center justify-center gap-2",
            "rounded-xl",
            "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500",
            "px-4 text-[11px] font-bold text-white",
            "shadow-[0_8px_20px_rgba(79,70,229,0.22)]",
            "transition-all duration-200",
            "hover:-translate-y-0.5",
            "hover:shadow-[0_11px_25px_rgba(79,70,229,0.28)]",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "sm:min-h-11 sm:px-6 sm:text-sm",
          )}
        >
          <span className="truncate">Voir les modèles recommandés</span>

          <ArrowRightIcon aria-hidden size={18} weight="bold" className="shrink-0" />
        </button>
      </div>
    </section>
  );
};
